import * as React from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthProvider.jsx'
import { Button } from '@/components/ui/button.jsx'
import { Field } from '@/components/ui/field.jsx'
import { Alert } from '@/components/ui/feedback.jsx'

/**
 * Sign in (Phase 1b screen 1).
 *
 * The one screen that talks to Supabase Auth directly rather than to the MBOS
 * API — there is no token yet, so there is nothing the API could authenticate.
 *
 * It also renders BEFORE any tenant is known, which is why it uses the MBOS
 * default palette rather than tenant branding: on a shared dashboard host we
 * cannot know whose workspace is being signed into until the token exists.
 * Guessing from the last session would show one customer another customer's
 * brand on a shared laptop.
 */
export function LoginPage () {
  const { session, signIn } = useAuth()
  const location = useLocation()

  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState(null)
  const [submitting, setSubmitting] = React.useState(false)

  if (session) {
    // Return the user to whatever they were trying to reach, so a bookmarked
    // job link survives an expired session.
    const to = location.state?.from ?? '/'
    return <Navigate to={to} replace />
  }

  async function handleSubmit (event) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await signIn(email.trim(), password)
    } catch (err) {
      // Supabase returns one message for a wrong password and an unknown
      // address, deliberately — distinguishing them would confirm which email
      // addresses have accounts. Keep it that way.
      setError(
        err?.message === 'Invalid login credentials'
          ? 'That email and password do not match.'
          : err?.message ?? 'Could not sign in. Try again.'
      )
      setSubmitting(false)
    }
    // On success the auth listener swaps this screen out; leaving `submitting`
    // true avoids a flash of the enabled form during that hand-off.
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">MBOS</h1>
          <p className="mt-1.5 text-sm text-ink-muted">Sign in to your workspace</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 rounded-lg border border-line bg-surface p-6 shadow-card"
          noValidate
        >
          {error && <Alert tone="danger">{error}</Alert>}

          <Field
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            required
            disabled={submitting}
          />

          <Field
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={submitting}
          />

          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-brand hover:underline">
              Forgot password?
            </Link>
          </div>

          <Button type="submit" className="w-full" loading={submitting}>
            {submitting ? 'Signing in' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-ink-muted">
          Trouble signing in? Contact your workspace owner.
        </p>
      </div>
    </div>
  )
}
